import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RATE_IT_COLORS, RATE_IT_LAYOUT } from '../constants';

const Viewport = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  /* "mandatory" rather than "proximity": this is a one-moment-at-a-time feed, and a partially
     scrolled card would show two half-moments with two rating bars. */
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  background: ${RATE_IT_COLORS.CARD_BG};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  /* Below the desktop breakpoint the moment is full-bleed; above it, a centred 9:16 stage on
     black — a portrait clip stretched across a 21:9 viewport reads as broken, not immersive. */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media (min-width: ${RATE_IT_LAYOUT.DESKTOP_BREAKPOINT}px) {
    width: auto;
    aspect-ratio: 9 / 16;
    max-height: min(100%, ${RATE_IT_LAYOUT.STAGE_MAX_HEIGHT}px);
    border-radius: 16px;
  }
`;

/**
 * The desktop advance controls: WIDE AND FLAT, not round.
 *
 * They sit on the moment itself, top-centre and bottom-centre, where a 40px disc ate vertical
 * room the media needs — the top one crowding the header row, the bottom one the rating
 * instruction. A 22px-tall pill is the smallest shape that still clears the 44px-wide comfort
 * target horizontally while costing almost nothing in height.
 */
const Chevron = styled.button`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  ${(props) => (props.$dir === 'up' ? 'top: 14px;' : 'bottom: 14px;')}
  z-index: 30;
  width: 46px;
  height: 22px;
  padding: 0;
  border-radius: 11px;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  display: none;

  /* A mouse user has no swipe, so desktop gets explicit controls. Keyboard works everywhere. */
  @media (min-width: ${RATE_IT_LAYOUT.DESKTOP_BREAKPOINT}px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.65);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

/**
 * The arrowhead, drawn as two borders on a rotated box.
 *
 * A glyph rather than a text arrow: the previous buttons rendered a literal ↑/↓ character, whose
 * size and vertical centring come from the font's metrics, so it drifted off-centre inside a
 * short pill. Two borders and a rotation are exact at any size and need no asset.
 */
const Arrowhead = styled.span`
  width: 8px;
  height: 8px;
  border-top: 2px solid #fff;
  border-left: 2px solid #fff;
  border-radius: 1px;
  /* The rotated box's ink sits above its own centre, so nudge it back onto the pill's axis. */
  transform: ${(props) =>
    props.$dir === 'up'
      ? 'rotate(45deg) translate(1px, 1px)'
      : 'rotate(-135deg) translate(1px, 1px)'};
`;

/**
 * The vertical one-item-at-a-time pager — web's replacement for mobile's `PagerView`.
 *
 * CSS SCROLL-SNAP rather than a transform pager, deliberately. Snap gives touch swipe, trackpad
 * momentum and wheel scrolling for free, all with the platform's own physics. The obvious
 * alternative — absolutely positioning slides and animating `translateY` — looks closer to
 * `PagerView` but forces hand-rolled wheel accumulation, and trackpads emit hundreds of small
 * delta events, which is the classic way such a pager skips three items on one flick.
 *
 * The index is DERIVED from scroll position via `IntersectionObserver` rather than tracked by
 * the code that scrolls. That means a programmatic advance and a fan's own swipe report through
 * exactly one path, so the feed above cannot end up with an index that disagrees with what is on
 * screen.
 *
 * Isolated from `RateItFeed` on purpose: the feed's rating logic is a close port of mobile's and
 * should stay readable next to it, so everything web-shaped about navigation lives here.
 */
const RateItPager = forwardRef(({ count, renderSlide, onIndexChange, currentIndex }, ref) => {
  const { t } = useTranslation();
  const viewportRef = useRef(null);
  const slideRefs = useRef([]);

  const goTo = useCallback((index) => {
    const viewport = viewportRef.current;
    const slide = slideRefs.current[index];
    if (!viewport || !slide) return;

    viewport.scrollTo({ top: slide.offsetTop, behavior: 'smooth' });
  }, []);

  useImperativeHandle(ref, () => ({ goTo }), [goTo]);

  /**
   * One observer over all slides; the most visible one is the current index.
   *
   * Threshold 0.6 rather than 0.5: at exactly half, two slides can both qualify mid-scroll and
   * the index flickers between them, which would clear a pending auto-advance for no reason.
   */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;

        const index = slideRefs.current.indexOf(visible.target);
        if (index >= 0) onIndexChange(index);
      },
      { root: viewport, threshold: 0.6 }
    );

    slideRefs.current.filter(Boolean).forEach((slide) => observer.observe(slide));
    return () => observer.disconnect();
  }, [count, onIndexChange]);

  // Keyboard navigation. Without it the feed is unreachable for anyone not using a pointer, and
  // the snap container alone does not move focus between slides.
  useEffect(() => {
    const onKeyDown = (event) => {
      // Never hijack typing — the share step has a message field on the same screen.
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;

      if (event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ') {
        event.preventDefault();
        goTo(Math.min(currentIndex + 1, count - 1));
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        goTo(Math.max(currentIndex - 1, 0));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goTo, currentIndex, count]);

  return (
    <>
      <Viewport ref={viewportRef}>
        {Array.from({ length: count }, (unused, index) => (
          <Slide
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            ref={(node) => {
              slideRefs.current[index] = node;
            }}
          >
            <Stage>{renderSlide(index)}</Stage>
          </Slide>
        ))}
      </Viewport>

      <Chevron
        type="button"
        $dir="up"
        onClick={() => goTo(currentIndex - 1)}
        disabled={currentIndex <= 0}
        aria-label={t('tiri.rateIt.previousMoment', 'Previous moment')}
      >
        <Arrowhead $dir="up" />
      </Chevron>
      <Chevron
        type="button"
        $dir="down"
        onClick={() => goTo(currentIndex + 1)}
        disabled={currentIndex >= count - 1}
        aria-label={t('tiri.rateIt.nextMoment', 'Next moment')}
      >
        <Arrowhead $dir="down" />
      </Chevron>
    </>
  );
});

RateItPager.displayName = 'RateItPager';

export default RateItPager;
